/**
 * Unified Entity Search Component
 * Generic class for entity search with API or local data
 */
class EntitySearch {
    constructor(config) {
        this.config = {
            apiUrl: config.apiUrl,
            localData: config.localData || null,
            entityType: config.entityType,
            searchInputId: config.searchInputId,
            hiddenInputId: config.hiddenInputId,
            resultsId: config.resultsId,
            selectedInfoId: config.selectedInfoId,
            selectedNameId: config.selectedNameId,
            minLength: config.minLength || 2,
            debounceMs: config.debounceMs || 300,
            emptyMessage: config.emptyMessage || 'Ничего не найдено',
        };

        this.debounceTimer = null;
        this.activeIndex = -1;
        this.init();
    }

    init() {
        this.searchInput = document.getElementById(this.config.searchInputId);
        this.hiddenInput = document.getElementById(this.config.hiddenInputId);
        this.results = document.getElementById(this.config.resultsId);
        this.resultsList = this.results?.querySelector('.entity-results-list');
        this.selectedInfo = document.getElementById(this.config.selectedInfoId);
        this.selectedName = document.getElementById(this.config.selectedNameId);

        if (!this.searchInput) {
            console.error('EntitySearch: search input not found:', this.config.searchInputId);
            return;
        }
        if (!this.resultsList) {
            console.error('EntitySearch: results list not found in:', this.config.resultsId);
            return;
        }

        this.searchInput.addEventListener('input', (e) => {
            clearTimeout(this.debounceTimer);
            const query = e.target.value.trim();

            if (query.length < this.config.minLength) {
                this.hideResults();
                return;
            }

            this.debounceTimer = setTimeout(() => {
                this.search(query);
            }, this.config.debounceMs);
        });

        document.addEventListener('click', (e) => {
            if (!this.searchInput.contains(e.target) && !this.results?.contains(e.target)) {
                this.hideResults();
            }
        });

        this.searchInput.addEventListener('focus', () => {
            const query = this.searchInput.value.trim();
            if (query.length >= this.config.minLength) {
                this.search(query);
            }
        });

        this.searchInput.addEventListener('keydown', (e) => {
            this.handleKeyboard(e);
        });
    }

    async search(query) {
        if (this.config.localData) {
            const filtered = this.config.localData.filter(item => {
                const searchText = (item.name + ' ' + (item.subtitle || '')).toLowerCase();
                return searchText.includes(query.toLowerCase());
            });
            this.renderResults(filtered.map(item => ({
                id: item.id,
                name: item.name,
                subtitle: item.subtitle
            })));
            return;
        }

        try {
            const url = `${this.config.apiUrl}?q=${encodeURIComponent(query)}&type=${this.config.entityType}`;
            const response = await fetch(url);
            const data = await response.json();

            if (data.success) {
                this.renderResults(data.data);
            } else {
                this.showEmpty();
            }
        } catch (error) {
            console.error('EntitySearch error:', error);
            this.showEmpty();
        }
    }

    renderResults(items) {
        if (!items || items.length === 0) {
            this.showEmpty();
            return;
        }

        this.resultsList.innerHTML = items.map(item => `
            <button type="button"
                    class="list-group-item list-group-item-action entity-result-item"
                    data-id="${item.id}"
                    data-name="${this.escapeHtml(item.name)}"
                    data-phone="${item.phone ? this.escapeHtml(item.phone) : ''}">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <div class="fw-medium">${this.highlightMatch(item.name)}</div>
                        ${item.subtitle ? `<small class="text-muted">${this.escapeHtml(item.subtitle)}</small>` : ''}
                    </div>
                    ${item.badge ? `<span class="badge bg-${item.badge.color}">${item.badge.text}</span>` : ''}
                </div>
            </button>
        `).join('');

        this.resultsList.querySelectorAll('.entity-result-item').forEach(btn => {
            btn.addEventListener('click', () => {
                const extraData = {};
                if (btn.dataset.phone) extraData.phone = btn.dataset.phone;
                this.selectItem(btn.dataset.id, btn.dataset.name, extraData);
            });
        });

        this.results.style.display = 'block';
        this.activeIndex = -1;
    }

    showEmpty() {
        this.resultsList.innerHTML = `
            <div class="list-group-item text-muted text-center py-3">
                <i class="bi bi-inbox fs-4 d-block mb-2"></i>
                ${this.config.emptyMessage}
            </div>
        `;
        this.results.style.display = 'block';
    }

    hideResults() {
        if (this.results) {
            this.results.style.display = 'none';
        }
        this.activeIndex = -1;
    }

    selectItem(id, name, extraData = {}) {
        this.hiddenInput.value = id;
        this.searchInput.value = name;
        if (this.selectedName) {
            this.selectedName.textContent = name;
        }
        if (this.selectedInfo) {
            this.selectedInfo.classList.remove('d-none');
        }
        this.hideResults();

        this.searchInput.dispatchEvent(new CustomEvent('entity:selected', {
            detail: { id, name, ...extraData }
        }));
    }

    handleKeyboard(e) {
        const items = this.resultsList?.querySelectorAll('.entity-result-item');
        if (!items || items.length === 0) return;

        switch(e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
                this.highlightItem(items);
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.activeIndex = Math.max(this.activeIndex - 1, 0);
                this.highlightItem(items);
                break;
            case 'Enter':
                e.preventDefault();
                if (this.activeIndex >= 0 && items[this.activeIndex]) {
                    items[this.activeIndex].click();
                }
                break;
            case 'Escape':
                this.hideResults();
                break;
        }
    }

    highlightItem(items) {
        items.forEach((item, index) => {
            item.classList.toggle('active', index === this.activeIndex);
        });
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    highlightMatch(text) {
        const query = this.searchInput.value.trim();
        if (!query) return this.escapeHtml(text);

        const regex = new RegExp(`(${this.escapeHtml(query)})`, 'gi');
        return this.escapeHtml(text).replace(regex, '<mark>$1</mark>');
    }
}

// Backward compatibility for inline scripts that rely on window.EntitySearch
window.EntitySearch = EntitySearch;

export default EntitySearch;
